import { Button, Card, CardContent, Link, styled, Typography } from '@mui/material'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import moment from 'moment'
import React from 'react'

import TabHeader from '@/app/component/atom/tab-header'
import SubmitButton from '@/app/component/form/submit-button'

type Props = {
  isLoading: boolean

  onCancel: () => void
  onCreate: (date: string) => Promise<boolean>
}

const MIN_DATE = moment('2022-01-01T00:00:00Z')

const SmsReport: React.FC<Props> = ({ isLoading, onCancel, onCreate }) => {
  const [date, setDate] = React.useState<moment.Moment>(moment())
  const [lastSubmitted, setLastSubmitted] = React.useState<moment.Moment | null>(null)

  const handleSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault()

    if (!date) {
      return
    }

    const success = await onCreate(date.format('YYYY-MM'))
    if (success) {
      setLastSubmitted(date)
    }
  }

  const isBeforeMinDate = date && date.isBefore(MIN_DATE)
  const isAfterNow = date && date.isAfter(moment())
  const isSameAsLastSubmitted = !!lastSubmitted && lastSubmitted.isSame(date)

  const disabled = !date || isBeforeMinDate || isAfterNow || isSameAsLastSubmitted

  return (
    <Card>
      <TabHeader title="SMS Deliverability Report" />
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Create a report showing daily SMS delivery rates per country for any month. Access the report in{' '}
          <Link href="/exports">Exports</Link>
        </Typography>
        <Divider />
        <FormContainer onSubmit={handleSubmit}>
          <div>
            <DesktopDatePicker
              className="sms-report-date-picker"
              disableFuture
              format="MM/YYYY"
              label="Month and Year"
              minDate={MIN_DATE}
              value={date}
              views={['year', 'month']}
              onChange={(v) => v && setDate(v)}
            />
          </div>
          <Divider />
          <ButtonContainer>
            <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <SubmitButton disabled={disabled} isDirty isSubmitting={isLoading}>
              Generate
            </SubmitButton>
          </ButtonContainer>
        </FormContainer>
      </CardContent>
    </Card>
  )
}

const Divider = styled('div')({
  height: '20px',
  width: '100%',
})

const FormContainer = styled('form')({
  display: 'block',
})

const ButtonContainer = styled('div')({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
})

export default SmsReport
